import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	InputAdornment,
	IconButton,
	Menu,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { OrdemProducaoService } from "../../service/ordem_producao_service";
import ModalVisualizarOP from "../../components/ModalVisualizarOP";

function OrdemProducao() {
	const navigate = useNavigate();
	const [ordensProducao, setOrdensProducao] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [ordemVisualizada, setOrdemVisualizada] = useState(null);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	const loadOrdens = useCallback(async () => {
		setLoading(true);
		try {
			const ordemProducaoService = new OrdemProducaoService();
			const result = await ordemProducaoService.getAll(unidadeDistribuidoraId, searchTerm);
			setOrdensProducao(result || []);
		} catch (error) {
			console.error("Erro ao carregar ordens de produção:", error);
			alert(error.message || "Erro ao carregar ordens de produção");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, searchTerm]);

	useEffect(() => {
		if (unidadeDistribuidoraId) {
			loadOrdens();
		}
	}, [unidadeDistribuidoraId, loadOrdens]);

	// Debounced search effect
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (unidadeDistribuidoraId) {
				loadOrdens();
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm]);

	const handleNovaCadastro = () => {
		navigate("/ordem-producao/cadastro");
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleOpenMenu = (event, item) => {
		setAnchorEl(event.currentTarget);
		setSelectedItem(item);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedItem(null);
	};

	const handleVisualizar = () => {
		if (selectedItem) {
			setOrdemVisualizada(selectedItem);
		}
		handleCloseMenu();
	};

	const handleEdit = () => {
		if (selectedItem) {
			navigate(`/ordem-producao/cadastro/${selectedItem.id}`);
		}
		handleCloseMenu();
	};

	const handleDeleteClick = () => {
		setItemToDelete(selectedItem);
		setOpenDialog(true);
		handleCloseMenu();
	};

	const handleConfirmDelete = async () => {
		if (itemToDelete) {
			try {
				const ordemProducaoService = new OrdemProducaoService();
				await ordemProducaoService.delete(itemToDelete.id);
				setOpenDialog(false);
				setItemToDelete(null);
				loadOrdens();
			} catch (error) {
				console.error("Erro ao excluir ordem de produção:", error);
				alert(error.message || "Erro ao excluir ordem de produção");
			}
		}
	};

	const handleCancelDelete = () => {
		setOpenDialog(false);
		setItemToDelete(null);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
	};

	return (
		<AppLayout currentPage="ordem-producao">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com busca */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
						}}
					>
						<Typography
							variant="h4"
							sx={{
								color: "#666",
								fontWeight: 600,
							}}
						>
							Ordens de Produção
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<TextField
								size="small"
								placeholder="Buscar..."
								value={searchTerm}
								onChange={handleSearchChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{
									width: "300px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleNovaCadastro}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
									},
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Nova Ordem de Produção
							</Button>
						</Box>
					</Box>

					{/* Tabela */}
					<Paper
						sx={{
							bgcolor: "white",
							borderRadius: "12px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
							overflow: "hidden",
						}}
					>
						{loading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "400px",
								}}
							>
								<CircularProgress sx={{ color: "#1a9e8f" }} />
							</Box>
						) : ordensProducao.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "400px",
									gap: 2,
									p: 4,
								}}
							>
								<Typography
									variant="h6"
									sx={{ color: "#999", textAlign: "center" }}
								>
									Nenhuma ordem de produção encontrada
								</Typography>
								<Typography
									variant="body2"
									sx={{ color: "#999", textAlign: "center" }}
								>
									{searchTerm
										? "Tente fazer uma busca diferente"
										: 'Clique no botão "Nova Ordem de Produção" para começar'}
								</Typography>
							</Box>
						) : (
							<TableContainer>
								<Table>
									<TableHead>
										<TableRow sx={{ bgcolor: "#f5f5f5" }}>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Número OP
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Produto Final
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Quant. Entrada
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Quant. Final
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Embalagem
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Volumes
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Data
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Lote Rastreabilidade
											</TableCell>
											<TableCell
												sx={{ fontWeight: 600, color: "#666", width: "80px" }}
												align="center"
											>
												Ações
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{ordensProducao.map((op) => (
											<TableRow
												key={op.id}
												onClick={() => setOrdemVisualizada(op)}
												sx={{
													cursor: "pointer",
													"&:hover": {
														bgcolor: "#f9f9f9",
													},
												}}
											>
												<TableCell>
													<Typography variant="body2" sx={{ fontWeight: 500 }}>
														{op.numeroOP}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{op.produtoFinal?.cultivo?.cultura || "-"}
													</Typography>
													{op.produtoFinal?.cultivo?.variedade && (
														<Typography
															variant="caption"
															sx={{ color: "#999", display: "block" }}
														>
															{op.produtoFinal.cultivo.variedade}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{op.quantidadeEntrada.toLocaleString("pt-BR")}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{op.quantidadeFinal.toLocaleString("pt-BR")}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{op.embalagemProdutoFinal?.descricao || "-"}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{op.volumes}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{formatDate(op.data)}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{op.loteRastreabilidade}
													</Typography>
												</TableCell>
												<TableCell
													align="center"
													onClick={(e) => e.stopPropagation()}
												>
													<IconButton
														size="small"
														onClick={(e) => handleOpenMenu(e, op)}
														sx={{ color: "#666" }}
													>
														<MoreVertIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>
			</Box>

			{/* Menu de ações */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleVisualizar}>
					<VisibilityIcon fontSize="small" sx={{ mr: 1, color: "#1976d2" }} />
					Visualizar
				</MenuItem>
				<MenuItem onClick={handleEdit}>
					<EditIcon fontSize="small" sx={{ mr: 1, color: "#1a9e8f" }} />
					Editar
				</MenuItem>
				<MenuItem onClick={handleDeleteClick}>
					<DeleteIcon fontSize="small" sx={{ mr: 1, color: "#ff4d4d" }} />
					Excluir
				</MenuItem>
			</Menu>

			{/* Dialog de confirmação */}
			<Dialog open={openDialog} onClose={handleCancelDelete}>
				<DialogTitle>Confirmar Exclusão</DialogTitle>
				<DialogContent>
					<Typography>
						Tem certeza que deseja excluir a ordem de produção "OP {itemToDelete?.numeroOP}"?
					</Typography>
					<Typography variant="caption" sx={{ color: "#999", mt: 1, display: "block" }}>
						Esta ação não poderá ser desfeita.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDelete} sx={{ color: "#666" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirmDelete}
						variant="contained"
						sx={{
							bgcolor: "#ff4d4d",
							"&:hover": {
								bgcolor: "#e64545",
							},
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>

			<ModalVisualizarOP
				open={Boolean(ordemVisualizada)}
				ordem={ordemVisualizada}
				onClose={() => setOrdemVisualizada(null)}
			/>
		</AppLayout>
	);
}

export default OrdemProducao;
